import { jsx as _jsx } from "react/jsx-runtime";
import { useLocation } from 'react-router-dom';
import { isObject } from 'lodash-es';
import { useMediaQueries, useOverlayContext } from 'seagull';
import { useIoContext, useStore } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useIsAppleSignInVisible, useIsNativeAppleSignIn } from '~/hooks/useAppleSignIn';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/error-response/ErrorResponse';
import { handleRequest, sendErrReport } from '~/infra/utils';
import { emailFromIdToken, gtmHashedEmailField } from '~/loginTelemetry/gtmHashedEmail';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { errorMessages } from '~/pages/unAuthorizedPages/constants';
import { WEB_APPLE_SDK_LOAD_FAILED } from '~/providers/IoContext';
import { useStoreActions } from '~/store/store-context';
import { usePostAuthRedirect } from '~/utils/auth-redirect';
import { AppleNameCaptureModal } from './AppleNameCaptureModal';
import { AppleSignInButton } from './AppleSignInButton';
const APPLE_AUTH_SCOPES = [
    'email',
    'name'
];
const APPLE_WEB_USER_CANCELLED_ERROR = 'popup_closed_by_user';
const REPORTABLE_APPLE_WEB_ERRORS = [
    'invalid_client',
    'invalid_request',
    'invalid_scope',
    'invalid_redirect_uri'
];
export function AppleButton(props) {
    const { ensureEmail, testId, width, promptForMissingName, onAuthSuccess, onAuthError } = props;
    const { reportToGtm, reportToAppcues, reportToHubspot, reportToHeap } = useEventReporter();
    const { api } = useStoreActions();
    const { mobile } = useMediaQueries();
    const { showModal } = useOverlayContext();
    const getUtms = useUtmsParams();
    const { native, browser: { getOrigin, webAppleAuth, crypto: { sha256Hex } }, cookies } = useIoContext();
    const useAuthorizationHeader = useStore((state)=>state.session.useAuthorizationHeader);
    const { pathname } = useLocation();
    const performPostAuthRedirect = usePostAuthRedirect();
    const { loginSucceeded, loginFailed } = useLoginTelemetry();
    const { t: bootRetryTexts } = useLocaleKeys('common.bootRetry');
    const nativeAppleSignIn = useIsNativeAppleSignIn();
    const appleSignInVisible = useIsAppleSignInVisible();
    const resolvedWidth = width !== null && width !== void 0 ? width : mobile ? 280 : 400;
    const resolvedTestId = testId || 'apple-login';
    function reportAuthFailure(label) {
        sendErrReport(label);
        loginFailed(LoginMethod.Apple);
        onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
    }
    function handleNativeAuthError(error) {
        if (isObject(error) && 'code' in error && error.code === 'USER_CANCELLED') return;
        loginFailed(LoginMethod.Apple);
        onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
    }
    function appleWebErrorCode(error) {
        if (!isObject(error) || !('error' in error)) return null;
        return String(error.error);
    }
    function isWebSdkLoadFailure(error) {
        return isObject(error) && 'code' in error && error.code === WEB_APPLE_SDK_LOAD_FAILED;
    }
    function handleWebAuthError(error) {
        if (isWebSdkLoadFailure(error)) {
            loginFailed(LoginMethod.Apple, 'sdk_load_failed');
            onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
            return;
        }
        if (error instanceof Error) {
            reportAuthFailure("AppleButton - web: ".concat(error.message));
            return;
        }
        const appleErrorCode = appleWebErrorCode(error);
        if (appleErrorCode === APPLE_WEB_USER_CANCELLED_ERROR) return;
        if (appleErrorCode != null && REPORTABLE_APPLE_WEB_ERRORS.includes(appleErrorCode)) {
            reportAuthFailure("AppleButton - web apple error: ".concat(appleErrorCode));
            return;
        }
        // WHY: allow-list only — an unrecognised Apple code could be another silent user-dismiss variant, so it surfaces to the user but stays out of Sentry.
        loginFailed(LoginMethod.Apple);
        onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
    }
    async function onAppleAuth(result, nonce) {
        var _result_firstName, _result_lastName;
        const authToken = await handleRequest(api.auth.oauthSignIn({
            provider: 'apple',
            pageUrl: getOrigin() + pathname,
            idToken: result.credential,
            apple: {
                firstName: ((_result_firstName = result.firstName) === null || _result_firstName === void 0 ? void 0 : _result_firstName.trim().slice(0, 100)) || undefined,
                lastName: ((_result_lastName = result.lastName) === null || _result_lastName === void 0 ? void 0 : _result_lastName.trim().slice(0, 100)) || undefined,
                nonce
            },
            utms: getUtms(),
            ensureEmail
        }));
        if (!authToken.success) {
            if (authToken.error.status === 403 || authToken.error.body === 'invalid auth request: email does not match') {
                loginFailed(LoginMethod.Apple, 'email_does_not_match');
                onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError(errorMessages.noBusinessEmailError);
                return;
            }
            if (authToken.error.hasServerError(ServerError.EmailIsInvalid)) {
                onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError(errorMessages.noBusinessEmailError);
                loginFailed(LoginMethod.Apple, 'invalid_email');
            } else if (authToken.error.hasServerError(ServerError.AccountIsDisabled)) {
                loginFailed(LoginMethod.Apple, 'account_disabled');
                onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
            } else if (authToken.error.isTransportFailure()) {
                loginFailed(LoginMethod.Apple, 'network_error');
                onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError(bootRetryTexts.body());
            } else {
                const errorCode = authToken.error.serverErrorCode();
                reportAuthFailure("AppleButton - ".concat(errorCode !== null && errorCode !== void 0 ? errorCode : "status ".concat(authToken.error.status)));
            }
        } else if (authToken.data != null) {
            var _result_firstName1, _result_lastName1;
            if (native.isNative && useAuthorizationHeader) {
                cookies.setAuthTokenForNative(authToken.data);
            }
            reportToGtm('form-success', 'apple-sign-in', undefined, await gtmHashedEmailField(sha256Hex, emailFromIdToken(result.credential)));
            reportToAppcues('form-success', 'apple-sign-in');
            reportToHubspot('apple_sign_in');
            reportToHeap('appleSignIn', 'form-success', 'apple-sign-in');
            const nameMissing = !((_result_firstName1 = result.firstName) === null || _result_firstName1 === void 0 ? void 0 : _result_firstName1.trim()) && !((_result_lastName1 = result.lastName) === null || _result_lastName1 === void 0 ? void 0 : _result_lastName1.trim());
            if (promptForMissingName && nameMissing) {
                await showModal({
                    biName: 'apple_name_capture',
                    closeOnClickOutside: false,
                    closeOnEscapeKey: false,
                    content: (param)=>{
                        let { dismissModal } = param;
                        return /*#__PURE__*/ _jsx(AppleNameCaptureModal, {
                            dismissModal: dismissModal
                        });
                    }
                });
            }
            onAuthSuccess === null || onAuthSuccess === void 0 ? void 0 : onAuthSuccess();
            loginSucceeded(LoginMethod.Apple);
            performPostAuthRedirect();
        } else {
            sendErrReport('AppleButton - did not retrieve an auth token');
            loginFailed(LoginMethod.Apple);
            onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
        }
    }
    async function authenticateNative(param) {
        let { nonce, scopes } = param;
        const hashedNonce = await sha256Hex(nonce);
        return native.appleAuth.signIn({
            nonce: hashedNonce,
            scopes
        });
    }
    async function authenticateWeb(param) {
        let { nonce, scopes } = param;
        const hashedNonce = await sha256Hex(nonce);
        return webAppleAuth.signIn({
            nonce: hashedNonce,
            scopes
        });
    }
    if (nativeAppleSignIn) {
        return /*#__PURE__*/ _jsx(AppleSignInButton, {
            testId: resolvedTestId,
            width: resolvedWidth,
            biName: "native_apple_signin",
            scopes: APPLE_AUTH_SCOPES,
            authenticate: authenticateNative,
            onCredential: onAppleAuth,
            onError: handleNativeAuthError
        });
    }
    if (appleSignInVisible) {
        return /*#__PURE__*/ _jsx(AppleSignInButton, {
            testId: resolvedTestId,
            width: resolvedWidth,
            biName: "apple_signin",
            scopes: APPLE_AUTH_SCOPES,
            authenticate: authenticateWeb,
            onCredential: onAppleAuth,
            onError: handleWebAuthError
        });
    }
    return null;
}
